import { useNavigate } from '@tanstack/react-router';
import { ChevronLeft, FileText, Hash, Hexagon, Leaf, Shield, Sprout, type LucideIcon } from 'lucide-react';
import { Avatar } from '@/ui/primitives';
import { useAgentes, useCanais } from '../api';

const DOMINIO_LABEL: Record<string, string> = {
  credito: 'Operações de crédito',
  fundiario: 'Fundiário',
  ambiental: 'Ambiental',
  agronomia: 'Agronomia',
};
const ICON: Record<string, LucideIcon> = {
  poly: Hexagon,
  doc: FileText,
  leaf: Leaf,
  sprout: Sprout,
  shield: Shield,
};

// Fundo de marca por banco (logos com cor que sumiria no branco precisam do fundo
// da instituição) — cor é DADO da marca, vai inline igual à exceção de cor de avatar.
const BANK_BG: Record<string, string> = {
  brasil: '#f9d616', // amarelo BB (logo azul)
  bradesco: '#cc092f', // vermelho Bradesco (logo branco)
  caixa: '#005ca9', // azul Caixa (logo laranja/branco)
};

// Cor do círculo do canal por slug (Rebeca/Flora) — fiel ao Figma (Geo=azul,
// CCIR/CAR=verde, Prada=teal, Licenciamento=azul, Desembargos=âmbar). Cor é dado,
// vai inline igual à exceção de cor de avatar. Fallback por hash p/ canais novos.
const CANAL_COR: Record<string, string> = {
  georreferenciamento: '#4a8fd6',
  ccir: '#3f9c5c',
  car: '#3f9c5c',
  prada: '#14a098',
  licenciamento: '#4a8fd6',
  desembargos: '#d9a43a',
};
const CANAL_PALETTE = ['#4a8fd6', '#3f9c5c', '#14a098', '#d9a43a', '#6366f1', '#a855f7'];
function corCanal(slug: string): string {
  if (CANAL_COR[slug]) return CANAL_COR[slug]!;
  let h = 0;
  for (let i = 0; i < slug.length; i += 1) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return CANAL_PALETTE[h % CANAL_PALETTE.length]!;
}

// Avatar do canal: logo da instituição (logo:<banco>) no círculo da marca; 'hash'
// (Geral) em coral; demais ícones em círculo COLORIDO (por slug) com ícone branco.
function CanalAvatar({ icone, nome, slug }: { icone: string | null; nome: string; slug: string }) {
  if (icone?.startsWith('logo:')) {
    const bank = icone.slice(5);
    const bg = BANK_BG[bank] ?? '#ffffff';
    return (
      <span
        className="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-full"
        style={{ background: bg }}
      >
        <img src={`/logos/${bank}.svg`} alt={nome} className="size-7 object-contain" />
      </span>
    );
  }
  if (!icone || icone === 'hash') {
    return (
      <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]">
        <Hash size={20} strokeWidth={2.25} className="text-[var(--on-solid)]" />
      </span>
    );
  }
  const Icon = ICON[icone] ?? Hash;
  return (
    <span
      className="flex size-11 shrink-0 items-center justify-center rounded-full"
      style={{ background: corCanal(slug) }}
    >
      <Icon size={20} strokeWidth={2} className="text-white" />
    </span>
  );
}

export function CanaisView({ agente }: { agente: string }) {
  const navigate = useNavigate();
  const { data: agentes } = useAgentes();
  const ag = (agentes ?? []).find((a) => a.id === agente);
  const { data: canais, isLoading, isError } = useCanais(agente);
  const n = (canais ?? []).length;

  return (
    <div className="flex h-full flex-col bg-bg">
      {/* header */}
      <header className="flex h-16 shrink-0 items-center gap-2.5 border-b border-border bg-surface px-3">
        <button
          type="button"
          onClick={() => void navigate({ to: '/agentes' })}
          className="flex size-9 shrink-0 items-center justify-center rounded-full text-text hover:bg-surface2"
          aria-label="Voltar"
        >
          <ChevronLeft size={26} strokeWidth={2} />
        </button>
        <Avatar name={ag?.nome ?? agente} size="lg" {...(ag?.cor ? { color: ag.cor } : {})} />
        <div className="flex min-w-0 flex-col">
          <span className="text-[1.05rem] font-bold text-text truncate leading-tight">{ag?.nome ?? agente}</span>
          <span className="text-[0.74rem] text-text-2 truncate leading-tight">
            {DOMINIO_LABEL[ag?.dominio ?? ''] ?? ag?.dominio ?? ''} · {n} canais
          </span>
        </div>
      </header>

      {/* lista */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <p className="px-4 pt-4 pb-1 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">Canais</p>
        {isLoading && (
          <div className="flex flex-col gap-2 p-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 rounded-xl bg-surface2 animate-pulse" />
            ))}
          </div>
        )}
        {isError && <p className="p-4 text-sm text-text-2">Não foi possível carregar os canais.</p>}
        {(canais ?? []).map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => void navigate({ to: '/agentes/$agente/$canal', params: { agente, canal: c.slug } })}
            className="flex w-full items-center gap-3 border-b border-border last:border-b-0 px-4 py-3 text-left hover:bg-surface2/60"
          >
            <CanalAvatar icone={c.icone} nome={c.nome} slug={c.slug} />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-[0.95rem] font-semibold text-text truncate">{c.nome}</span>
              <span className="text-[0.82rem] text-text-2 truncate">{c.ultima_preview || 'Toque para conversar'}</span>
            </div>
            {c.nao_lidas > 0 && (
              <span className="inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-[var(--accent)] px-1.5 text-[0.68rem] font-bold leading-none text-[var(--on-solid)]">
                {c.nao_lidas > 99 ? '99+' : c.nao_lidas}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
